/*
  Shop the look.

  Each look is a media panel with dot markers and a product list beside it.
  Markers and rows are two views of the same eight slots, paired by the index
  in data-marker and data-row, so the pairing survives the merchant leaving
  gaps in product_1 through product_8.

  Three jobs: keep marker and row highlighting in step, gate Add all to cart on
  every multi-variant row having a selection, and play a look's video only
  while that look is the one on screen.

  It does not extend SliderComponent. The carousel is a plain
  <slider-component> inside this element and Dawn drives it unchanged; this
  component only listens to it.
*/
class ShopTheLook extends HTMLElement {
  connectedCallback() {
    this.looks = Array.from(this.querySelectorAll('.shop-the-look__look'));
    if (this.looks.length === 0) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.looks.forEach((look) => this.setupLook(look));
    this.setupVideo();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /* Pairing -------------------------------------------------------------- */

  setupLook(look) {
    const markers = Array.from(look.querySelectorAll('[data-marker]'));
    const rows = Array.from(look.querySelectorAll('[data-row]'));

    const rowFor = (index) => rows.find((row) => row.dataset.row === index);
    const markerFor = (index) => markers.find((marker) => marker.dataset.marker === index);

    const highlight = (index, on) => {
      rowFor(index)?.classList.toggle('shop-the-look__row--active', on);
      markerFor(index)?.classList.toggle('shop-the-look__marker--active', on);
    };

    /*
      focus and blur rather than focusin and focusout: a row contains its own
      links and a select, and focusin would fire for those too, which is
      correct here - moving into anything inside the row should light its
      marker. So rows use focusin/focusout and markers, having no focusable
      children, use focus/blur.
    */
    markers.forEach((marker) => {
      const index = marker.dataset.marker;
      marker.addEventListener('mouseenter', () => highlight(index, true));
      marker.addEventListener('mouseleave', () => highlight(index, false));
      marker.addEventListener('focus', () => highlight(index, true));
      marker.addEventListener('blur', () => highlight(index, false));
      marker.addEventListener('click', () => {
        const row = rowFor(index);
        if (!row) return;
        /*
          block: 'nearest' so a row already in view does not jolt the page, and
          because the list is the scrolling box here, not the window.
        */
        row.scrollIntoView({ block: 'nearest', behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
        rows.forEach((other) => other.classList.remove('shop-the-look__row--selected'));
        row.classList.add('shop-the-look__row--selected');
        highlight(index, true);
      });
    });

    rows.forEach((row) => {
      const index = row.dataset.row;
      row.addEventListener('mouseenter', () => highlight(index, true));
      row.addEventListener('mouseleave', () => highlight(index, false));
      row.addEventListener('focusin', () => highlight(index, true));
      row.addEventListener('focusout', (event) => {
        if (!row.contains(event.relatedTarget)) highlight(index, false);
      });
    });

    /* Add all ------------------------------------------------------------ */

    const button = look.querySelector('[data-add-all]');
    const status = look.querySelector('[data-status]');
    if (!button) return;

    look.querySelectorAll('[data-variant-select]').forEach((select) => {
      select.addEventListener('change', () => this.refreshAddAll(look));
    });
    button.addEventListener('click', () => this.addAll(look, button, status));

    this.refreshAddAll(look);
  }

  /* Gating --------------------------------------------------------------- */

  /*
    A row contributes a variant if it is not sold out and either carries a
    single variant id from Liquid or has a selection made in its select. Sold
    out rows are excluded rather than blocking, so a look with one sold out
    item can still add the rest.
  */
  collect(look) {
    const items = [];
    let pending = 0;

    look.querySelectorAll('[data-row]').forEach((row) => {
      if (row.dataset.soldOut === 'true') return;

      if (row.dataset.variantId) {
        items.push({ id: Number(row.dataset.variantId), quantity: 1 });
        return;
      }

      const select = row.querySelector('[data-variant-select]');
      if (!select) return;
      if (select.value) {
        items.push({ id: Number(select.value), quantity: 1 });
      } else {
        pending += 1;
      }
    });

    return { items, pending };
  }

  refreshAddAll(look) {
    const button = look.querySelector('[data-add-all]');
    const status = look.querySelector('[data-status]');
    if (!button) return;

    const { items, pending } = this.collect(look);

    if (pending > 0) {
      button.disabled = true;
      this.setStatus(status, status?.dataset.chooseFirst || '');
      return;
    }

    if (items.length === 0) {
      button.disabled = true;
      this.setStatus(status, status?.dataset.nothingToAdd || '');
      return;
    }

    button.disabled = false;
    this.setStatus(status, '');
  }

  /*
    Every string comes off the status element's data attributes, translated in
    Liquid. Nothing user facing is written in this file.
  */
  setStatus(status, message, isError) {
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('shop-the-look__status--error', Boolean(isError));
  }

  /* Cart ----------------------------------------------------------------- */

  async addAll(look, button, status) {
    const { items, pending } = this.collect(look);
    if (pending > 0 || items.length === 0) return;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    button.disabled = true;
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
    this.setStatus(status, '');

    /*
      One request with an items array, not one per row. Shopify treats a
      multi-item add as all or nothing, so there is no partial state to unpick:
      either every line is on the cart or none is, and the catch below is
      reporting a failure rather than reconciling one.
    */
    const body = { items };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      cart.setActiveElement(document.activeElement);
    }

    /*
      The outcome is held and written after refreshAddAll rather than inside
      the try or the catch. refreshAddAll rewrites the status - that is its job,
      it owns the "choose an option first" prompt - so anything set before it
      runs is wiped. Setting the error inside the catch looked correct and left
      a failed add silently reporting nothing at all.
    */
    let outcome = null;

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      /*
        A rejected add still comes back as JSON with a description on it, so
        response.ok is checked before anything is treated as success. Reporting
        "added" off a 422 is the failure mode this guards.
      */
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || `Cart returned ${response.status}`);
      }

      if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }

      cart.renderContents(data);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shop-the-look', cartData: data });
      }
      outcome = { message: status?.dataset.added || '', isError: false };
    } catch (error) {
      console.error(error);
      outcome = { message: status?.dataset.error || error.message, isError: true };
    } finally {
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
      this.refreshAddAll(look);
      if (outcome) this.setStatus(status, outcome.message, outcome.isError);
    }
  }

  /* Video ---------------------------------------------------------------- */

  /*
    Only the look on screen plays. Same discipline as hero-slideshow: no
    autoplay attribute in the markup, playback owned here, and a hidden tab
    pauses everything rather than decoding video nobody is looking at.
  */
  setupVideo() {
    const videos = Array.from(this.querySelectorAll('.shop-the-look__look video'));
    if (videos.length === 0) return;

    const play = (video) => {
      if (this.reducedMotion.matches || document.hidden) return;
      const played = video.play();
      if (played) played.catch(() => {});
    };

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector('video');
            if (!video) return;
            if (entry.isIntersecting) play(video);
            else video.pause();
          });
        },
        { threshold: 0.6 }
      );
      this.looks.forEach((look) => this.observer.observe(look));
    }

    this.onVisibility = () => {
      if (document.hidden) videos.forEach((video) => video.pause());
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    this.reducedMotion.addEventListener('change', () => {
      if (this.reducedMotion.matches) videos.forEach((video) => video.pause());
    });
  }
}

customElements.define('shop-the-look', ShopTheLook);
